import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";

import isEmpty from "lodash/isEmpty";
import uniq from "lodash/uniq";
import sortBy from "lodash/sortBy";
import get from "lodash/get";
import capitalize from "lodash/capitalize";

import ProductDetailHeader from "@components/store/product-detail/shared/product-detail-header";
import SwagOptionsSelector from "@components/store/product-detail/shared/swag-options-selector";
import SwagMultipleOptionsSelector from "@components/store/product-detail/shared/swag-multiple-options-selector";
import ProductOptionSelector from "@components/store/product-detail/shared/product-option-selector";
import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";
import SizeChartModal from "@components/modals/size-chart";
import SwagViewOnlyVariantDetails from "@components/store/product-detail/shared/swag-view-only-variant-details";
import ProductInfoSection from "@components/store/product-detail/shared/product-info-section";
import ProductDescriptionAccordion from "@components/store/product-detail/shared/accordion/product-description";
import { getProductType, getProductCategory } from "@helpers/products/types";

import TippyTooltip from "@stadium/ui/src/components/tooltip/tippy-tooltip";
import { TextField } from "@stadium/ui/src/components/input/bs-input";

import { ProductDiscountTable } from "@components/store/product-detail/shared/discount-table";
import AddToBagLockerBtn from "@components/store/product-detail/shared/add-to-bag-locker-item";
import VendorHolidayMessage from "@components/store/product-detail/shared/vendor-holiday-message";
import ProductNotAvailableMessage from "@components/store/product-detail/shared/product-not-available-message";
import PropertiesAccordionList from "@components/store/product-detail/shared/properties-accordion-list";
import { ExternalButton, InternalButton } from "@sharedComponents/buttons";

import { normalizeCount } from "@stadium/helpers/form/formatter";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import { useCreateSwagContext } from "@contexts/create-swag-context";
import { useSendItemsContext } from "@contexts/send-items-context";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import usePopulateAutomatedCatalog from "@hooks/automated-catalog/use-populate-automated-catalog";

import { getIsLockerShop, getStoreCurrencyCode } from "@selectors/store";
import { getCartOrderLineItems, getCartOrder } from "@selectors/cart";

import { getMinOrderableQuantity } from "@helpers/products/search";
import {
  getQuantityFromSizeMapping,
  getTotalFromSizeMapping,
  getMinPriceForProduct,
  sortInvalidColorLast,
} from "@helpers/product-details";
import { getPriceFromBulkProduct } from "@stadium/helpers/product/utils";

import { useModal } from "@stadium/hooks/lib/use-modal";
import { useProductDetailContext } from "@contexts/product-detail-context";
import { useProductInfoContext } from "@contexts/product-info-context";
import { useFlags } from "@stadium/services/launch-darkly";
import { variantLevelMoqFlag } from "@stadium/services/launch-darkly/constants";

import useShowPrefrenceRestrictionModal from "@hooks/product-detail/use-show-prefrence-restriction-modal";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { SIZE_CHART_MODAL } from "@constants/modal-types";
import { ORDER_IN_BULK_OPTION_ID, STATE_DESIGN_COMPLETE } from "@constants/products/detail";
import { ANALYTICS_ATTRIBUTE_TYPE, ANALYTICS_TARGET_ATTRIBUTE } from "@constants/analytics/shop";
import { ANALYTICS_EVENT_ACTION_SELECTED } from "@stadium/services/analytics/constants/default";

import styles from "./main.module.scss";
import modalStyles from "./product-detail-modal.module.scss";

const DEFAULT_OBJ = {};

/** Build ordered size labels (presentation) that have qty > 0 — used for attribute_value payload. */
const getSizePresentationsWithPositiveQty = (availData, mapping) =>
  (availData || [])
    .filter(({ id }) => {
      const raw = mapping[id]?.qty;
      if (raw === undefined || raw === null || raw === "") return false;
      const n = Number(raw);
      return !Number.isNaN(n) && n > 0;
    })
    .map(({ id, presentation }) => ({
      value: presentation,
      quantity: Number(mapping[id]?.qty || 0),
    }));

export default function SwagProductDetail({
  product,
  optionTypesData,
  variantsData,
  optionTypeVariantMapping,
  allSizesVariants,
  selectedSizeVariant,
  setSelectedSizeVariant,
  setOptionTypeVariantMapping,
  productQuantity,
  setProductQuantity,
  discountTableData,
  sizeQtyMapping,
  setSizeQtyMapping,
  showProductName,
  canBuyProduct,
  editProductIds,
  selectBulkOptionInEditMode,
  renderMobileView,
  selectedPreferredColors,
  setSelectedPreferredColors,
}) {
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const withAnalytics = useWithAnalytics();

  /**
   * Bulk multi-size qty inputs fire attribute analytics on blur (not on each keystroke).
   * Ref-based Map: ensures UI state isolation per component instance while avoiding re-renders.
   *
   * On focus we stash the row's qty string; on blur we compare to the normalized input so we
   * only emit when the user changed something this visit.
   */
  const sizeQtyBlurBaselineRef = useRef(new Map());

  const { isSendToMyself } = useSendItemsContext();
  const { isSwagCreationShopFlow, preferredColorsHex } = useCreateSwagContext();
  const { addProductToAutomatedCatalog } = usePopulateAutomatedCatalog();
  const { [variantLevelMoqFlag]: variantLevelMoqFlagEnabled } = useFlags();

  const {
    hideVariantSelectionSection,
    hidePriceDetail,
    showAddToBox,
    isViewOnlyMode,
    renderCatalogModeFooterLinks,
    onSuccessCallback,
    renderCustomVariantSelectionSection,
  } = useProductInfoContext();

  const {
    name,
    priceIn,
    vendorByCountry,
    sizeChartEnabled,
    sizeChart,
    masterVariant,
    bulkAvailable,
    currentCatalog,
    sizeChartLink,
  } = product;

  const resolvedSelectedVariant = useMemo(
    () => (isEmpty(selectedSizeVariant) ? optionTypeVariantMapping : selectedSizeVariant),
    [optionTypeVariantMapping, selectedSizeVariant],
  );

  const { prices, amountIn } = useMemo(() => {
    const variant = masterVariant || product.variantsIncludingMaster[0];

    return {
      prices: variant?.prices?.map(({ priceIn }) => priceIn) || product.prices,
      amountIn: variant?.amountIn || product.amountIn,
    };
  }, [masterVariant, product]);

  const minOrderableQuantity = useMemo(
    () => getMinOrderableQuantity(product?.variantsIncludingMaster),
    [product],
  );

  const isVariantLevelMoqFlow =
    Boolean(variantLevelMoqFlagEnabled) && Boolean(product?.variantLevelCommerce);

  const minQty = useMemo(() => {
    if (isVariantLevelMoqFlow) {
      return Number(resolvedSelectedVariant?.minOrderableQuantity || minOrderableQuantity);
    }
    return minOrderableQuantity;
  }, [isVariantLevelMoqFlow, minOrderableQuantity, resolvedSelectedVariant]);

  const hasTierPricing = useMemo(
    () => resolvedSelectedVariant?.hasTierPricing || product.hasTierPricing,
    [product, resolvedSelectedVariant],
  );

  const [showBulkOptions, setShowBulkOptions] = useState(selectBulkOptionInEditMode);

  const { renderRestrictionsModal, renderAddToBagBtn, toggleRestrictionsModal } =
    useShowPrefrenceRestrictionModal({
      product,
      selectedVariant: resolvedSelectedVariant,
      isBYOProduct: showAddToBox,
      showBulkOptions,
    });

  const storeCurrencyCode = useSelector(getStoreCurrencyCode);

  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const { setIsOptionSelected, setIsColorSelected, setOptionSelectionError } =
    useProductDetailContext();
  const isLockerShop = useSelector(getIsLockerShop);
  const lineItems = useSelector(getCartOrderLineItems);
  const cartOrder = useSelector(getCartOrder);
  const isColorPresent = Boolean(optionTypesData.color);
  const hasSizeOptions = Boolean(optionTypesData.size);

  // Record qty when this size row gains focus; blur compares against this baseline (see handleSizeQtyBlur).
  const handleSizeQtyFocus = useCallback(
    (variantId) => {
      const raw = sizeQtyMapping[variantId]?.qty;
      const serialized = raw === undefined || raw === null ? "" : String(raw);
      sizeQtyBlurBaselineRef.current.set(variantId, serialized);
    },
    [sizeQtyMapping],
  );

  // Blur: emit attribute selected only if qty became positive AND differs from focus baseline.
  // attribute_value = single presentation string if one size has qty, else array of all sizes with qty > 0.
  // mappingSnapshot merges this row's blurred input so the payload matches what the user committed even under batched updates.
  const handleSizeQtyBlur = useCallback(
    (ev, variantId, presentation, availData) => {
      const normalized = normalizeCount(ev.target.value);
      const numVal = normalized === "" ? Number.NaN : Number(normalized);
      if (!presentation || Number.isNaN(numVal) || numVal <= 0) return;

      const baselineRaw = sizeQtyBlurBaselineRef.current.get(variantId) ?? "";
      const baselineNum = baselineRaw === "" ? 0 : Number(baselineRaw);
      if (numVal === baselineNum) return;

      const mappingSnapshot = {
        ...sizeQtyMapping,
        [variantId]: {
          ...sizeQtyMapping[variantId],
          qty: normalized,
        },
      };
      const list = getSizePresentationsWithPositiveQty(availData, mappingSnapshot);
      let attributeValue = list;
      if (list.length === 0) {
        attributeValue = [{ value: presentation, quantity: numVal }];
      }

      withAnalytics(undefined, {
        track: true,
        target: ANALYTICS_TARGET_ATTRIBUTE,
        action: ANALYTICS_EVENT_ACTION_SELECTED,
        eventProperties: {
          product_id: product.id,
          attribute_type: ANALYTICS_ATTRIBUTE_TYPE.SIZE,
          attribute_value: attributeValue,
          product_type: getProductType(product),
          category: getProductCategory(product),
        },
      })();
    },
    [product.id, sizeQtyMapping, withAnalytics],
  );

  // Clear baselines on unmount or product change.
  useEffect(() => {
    const map = sizeQtyBlurBaselineRef.current;
    return () => {
      map.clear();
    };
  }, [product.id]);

  const skipMinimumOrderableQuantity = cartOrder?.skipMinimumOrderableQuantity;

  const showBulkOrTierPricingOptions = showBulkOptions && hasTierPricing;
  const hasBulkAvailable = bulkAvailable || showBulkOrTierPricingOptions;
  const isPriceFormatInStoreCurrencyRequried = isLockerShop || bulkAvailable || hasTierPricing;

  const isCustomizable =
    isLockerShop && !(currentCatalog?.storeId && currentCatalog.state === STATE_DESIGN_COMPLETE);

  const { openModal: openSizeChartModal, renderModal: renderSizeChartModal } = useModal(
    SIZE_CHART_MODAL,
    SizeChartModal,
  );

  const quantity =
    (isLockerShop || hasBulkAvailable) && hasSizeOptions && isSendToMyself && !isVariantLevelMoqFlow
      ? getQuantityFromSizeMapping(sizeQtyMapping)
      : productQuantity;

  const handleAddProducts = async () => {
    const colorIds = selectedPreferredColors.map((color) => Number(color));
    await addProductToAutomatedCatalog({ productId: Number(product.id), colorIds });
    onSuccessCallback();
  };

  const handleOptionChange = useCallback(
    (data) => {
      const isBulkOptionSelected = data.id === ORDER_IN_BULK_OPTION_ID;
      setShowBulkOptions(isBulkOptionSelected);
      if (isBulkOptionSelected) return;
      isColorPresent ? setSelectedSizeVariant(data) : setOptionTypeVariantMapping(data);
    },
    [isColorPresent, setOptionTypeVariantMapping, setSelectedSizeVariant],
  );

  const sizeProps = useMemo(() => {
    const variantValues = sortBy(Object.values(variantsData), "position");

    if (isColorPresent)
      return {
        availData: allSizesVariants,
        selectedData: selectedSizeVariant,
        handleChange: handleOptionChange,
      };
    return {
      availData: variantValues,
      selectedData: selectBulkOptionInEditMode ? DEFAULT_OBJ : optionTypeVariantMapping,
      handleChange: handleOptionChange,
    };
  }, [
    allSizesVariants,
    handleOptionChange,
    isColorPresent,
    optionTypeVariantMapping,
    selectBulkOptionInEditMode,
    selectedSizeVariant,
    variantsData,
  ]);

  const selectedSize = !isEmpty(sizeProps?.selectedData);

  const availableSizes = useMemo(() => sizeProps.availData || [], [sizeProps]);
  const isSingleSize = useMemo(
    () => hasSizeOptions && availableSizes.length === 1,
    [hasSizeOptions, availableSizes],
  );

  const isMoqAddToBagBtnDisabled = useMemo(() => {
    return isVariantLevelMoqFlow && hasSizeOptions && isSendToMyself && !selectedSize;
  }, [isVariantLevelMoqFlow, hasSizeOptions, isSendToMyself, selectedSize]);

  const resetSizeQtyMapping = useCallback(() => {
    if (isSingleSize) {
      const [{ priceIn, id, hasTierPricing: tierOnSize }] = availableSizes;
      const price = tierOnSize ? getPriceFromBulkProduct(masterVariant, minQty) : priceIn;
      return setSizeQtyMapping({ [id]: { qty: minQty, storePrice: price, price } });
    }
    return setSizeQtyMapping({});
  }, [availableSizes, isSingleSize, masterVariant, minQty, setSizeQtyMapping]);

  useEffect(() => {
    if (isEmpty(editProductIds)) resetSizeQtyMapping();
  }, [availableSizes, editProductIds, resetSizeQtyMapping]);

  const getPriceRange = (prices = [], amountIn = "") => {
    const sortedPrices = [...(uniq(prices) || [])].sort((a, b) => a - b);
    if (sortedPrices?.length > 1) {
      return `${formatPriceInDisplayStoreCurrency(
        sortedPrices[0],
        storeCurrencyCode,
      )} - ${formatPriceInDisplayStoreCurrency(
        sortedPrices[sortedPrices.length - 1],
        storeCurrencyCode,
      )}`;
    } else if (sortedPrices[0]) {
      return formatPriceInDisplayStoreCurrency(sortedPrices[0], storeCurrencyCode);
    }
  };

  const getOptionTypeVariantPrice = () => {
    if (isEmpty(optionTypeVariantMapping)) {
      return getPriceRange(
        product.variantsIncludingMaster.map(({ priceIn }) => priceIn),
        product.variantsIncludingMaster.map(({ amountIn }) => amountIn),
      );
    }
    const { variants, amountIn } = optionTypeVariantMapping;
    return getPriceRange(
      variants?.map(({ priceIn }) => priceIn),
      amountIn,
    );
  };

  const getVariantPrice = () => {
    if (bulkAvailable || hasTierPricing) {
      const value = getPriceFromBulkProduct(masterVariant, quantity);
      return {
        display: formatPriceInDisplayStoreCurrency(value, storeCurrencyCode),
        value,
      };
    }
    return {
      display:
        formatPriceInDisplayStoreCurrency(selectedSizeVariant.priceIn, storeCurrencyCode) ||
        formatPriceInDisplayStoreCurrency(optionTypeVariantMapping.priceIn, storeCurrencyCode) ||
        getOptionTypeVariantPrice() ||
        getPriceRange(prices, amountIn),
      value: selectedSizeVariant.priceIn || optionTypeVariantMapping.priceIn || priceIn,
    };
  };

  const { value, display } = getVariantPrice();

  const computeTotalPrice = () => {
    const totalPriceForSizeOptions =
      bulkAvailable || hasTierPricing ? quantity * value : getTotalFromSizeMapping(sizeQtyMapping);
    return hasSizeOptions ? totalPriceForSizeOptions : quantity * value;
  };

  const totalPrice = computeTotalPrice();

  const handleQuantity = (newQuantity) => {
    setProductQuantity(newQuantity);
  };

  useEffect(() => {
    if (!isEmpty(allSizesVariants)) {
      !showBulkOptions && isEmpty(selectedSizeVariant)
        ? setIsOptionSelected(false)
        : setIsOptionSelected(true);
    }
  }, [allSizesVariants, selectedSizeVariant, showBulkOptions, setIsOptionSelected]);

  const handleSwagOptionSelectorChange = (data) => {
    // reset quantity on change of color
    if (data?.id !== optionTypeVariantMapping?.id) {
      setSizeQtyMapping({});
    }
    setOptionTypeVariantMapping(data);
    setOptionSelectionError(false);
  };

  const handleNewSwagColorChange = (nextSelected) => {
    setSelectedPreferredColors(nextSelected);
  };

  const colorVariantData = useMemo(
    () => sortBy(Object.values(variantsData), "position").sort(sortInvalidColorLast),
    [variantsData],
  );

  // Render helpers below are called from JSX; setState there re-ran every paint while guards stayed true
  // (infinite updates / frozen PDP). Sync context flags after commit instead.
  useEffect(() => {
    if (!isColorPresent || colorVariantData.length < 2) {
      setIsColorSelected(true);
    }
  }, [colorVariantData.length, isColorPresent, setIsColorSelected]);

  useEffect(() => {
    if (!hasSizeOptions) {
      setIsOptionSelected(true);
    }
  }, [hasSizeOptions, setIsOptionSelected]);

  const renderColorVariants = () => {
    if (!isColorPresent || colorVariantData.length < 2) {
      return null;
    }
    const selectedColor = optionTypeVariantMapping?.presentation;
    const title = selectedColor ? `COLOR: ${selectedColor}` : "AVAILABLE COLORS";

    return (
      <SwagOptionsSelector
        showImageOptions
        title={title}
        availData={colorVariantData}
        variantSelected={selectedSizeVariant}
        selectedData={optionTypeVariantMapping}
        handleChange={handleSwagOptionSelectorChange}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            attribute_type: ANALYTICS_ATTRIBUTE_TYPE.COLOR,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
        disabled={!canBuyProduct}
      />
    );
  };

  const getColorData = () => {
    return colorVariantData.reduce(
      (acc, color) => {
        if (preferredColorsHex.includes(color.name)) {
          acc.preferredColors.push(color);
        } else {
          acc.additionalColors.push(color);
        }
        return acc;
      },
      { additionalColors: [], preferredColors: [] },
    );
  };

  const renderPreferredColorAndSize = () => {
    if (!isColorPresent) {
      return null;
    }
    const { additionalColors, preferredColors } = getColorData();
    return (
      <div>
        {sizeProps?.availData?.length > 0 && (
          <>
            <h6 className={modalStyles.availableHeading}>available sizes</h6>
            <span className={`${modalStyles.availableHeading} ${modalStyles.sizeColor}`}>
              {sizeProps.availData.map((size) => size.presentation).join(", ")}
            </span>
          </>
        )}

        <h6 className={`${modalStyles.availableHeading} ${modalStyles.productColor}`}>
          Your preferred colors
        </h6>
        <SwagMultipleOptionsSelector
          availData={preferredColors}
          selectedData={selectedPreferredColors}
          handleChange={handleNewSwagColorChange}
          disabled={!canBuyProduct}
        />
        {additionalColors.length > 0 && (
          <>
            <h6 className={`${modalStyles.availableHeading} ${modalStyles.productColor}`}>
              additional colors
            </h6>
            <SwagMultipleOptionsSelector
              availData={additionalColors}
              selectedData={selectedPreferredColors}
              handleChange={handleNewSwagColorChange}
              disabled={!canBuyProduct}
            />
          </>
        )}
      </div>
    );
  };

  const renderAddProducts = () => {
    return (
      <div className={modalStyles.addCtaBlock}>
        <InternalButton
          onClick={() => handleAddProducts()}
          styleType="primary"
          className={modalStyles.addProductsCta}
        >
          Add products
        </InternalButton>
      </div>
    );
  };

  const updateSizeQtyMapping = useCallback(
    (variant) => {
      let newMapping = {};
      const availData = isColorPresent ? variant.variants : Object.values(variantsData);
      editProductIds.forEach((lineItemId) => {
        const lineItem = lineItems.find(({ id }) => id === lineItemId);
        if (!lineItem) return;
        const { productId, quantity } = lineItem;
        const addedVariant = availData.find(
          ({ externalId }) => productId.toString() === externalId,
        );
        if (!addedVariant) return;
        const { id, priceIn } = addedVariant;
        newMapping = {
          ...newMapping,
          [id]: {
            qty: quantity,
            storePrice: priceIn,
            lineItemId,
          },
        };
      });
      const newQty = getQuantityFromSizeMapping(newMapping);
      Object.keys(newMapping).forEach((idKey) => {
        newMapping[idKey].storePrice = getPriceFromBulkProduct(product.masterVariant, newQty);
      });
      setSizeQtyMapping(newMapping);
    },
    [
      editProductIds,
      isColorPresent,
      lineItems,
      product.masterVariant,
      variantsData,
      setSizeQtyMapping,
    ],
  );

  useEffect(() => {
    if (editProductIds && lineItems.length && !isEmpty(optionTypeVariantMapping)) {
      updateSizeQtyMapping(optionTypeVariantMapping);
    }
  }, [editProductIds, lineItems, optionTypeVariantMapping, updateSizeQtyMapping]);

  const renderSizeVariants = () => {
    if (!hasSizeOptions) {
      return null;
    }

    const title = get(sizeProps, "availData[0].optionTypePresentation", "Size");

    return (
      <ProductOptionSelector
        isColorPresent={isColorPresent}
        title={showBulkOptions ? renderSizeLabelAndChart(capitalize(title), minQty) : title}
        intent={product.intent}
        hasTierPricing={hasTierPricing && !isVariantLevelMoqFlow}
        {...sizeProps}
        showOptionSelector
        selectBulkOptionInEditMode={selectBulkOptionInEditMode}
        isSingleSize={isSingleSize}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            attribute_type: ANALYTICS_ATTRIBUTE_TYPE.SIZE,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
        disabled={!canBuyProduct}
      />
    );
  };

  const renderQuantitySelector = () => (
    <div className={styles.qtyBlock}>
      <span className={styles.qtyWrapper}>Quantity</span>
      <QuantitySelector
        quantity={quantity}
        setQuantity={setProductQuantity}
        className="defaultStyle"
        disabled={!canBuyProduct}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
      />
    </div>
  );

  const isVariantOutOfStock = () => {
    if (!isEmpty(selectedSizeVariant)) return selectedSizeVariant.outOfStock;
    else if (!isEmpty(optionTypeVariantMapping) && isEmpty(optionTypeVariantMapping.variants))
      return optionTypeVariantMapping.outOfStock;
    return !canBuyProduct;
  };

  const renderSizeChartCTA = () => {
    if (sizeChartEnabled) {
      return (
        <span onClick={openSizeChartModal} className={styles.sizeGuide}>
          Size Guide
        </span>
      );
    }
    if (sizeChartLink) {
      return (
        <ExternalButton
          styleType="link"
          href={sizeChartLink}
          className={styles.sizeGuide}
          target="_blank"
        >
          Size Guide
        </ExternalButton>
      );
    }
    return null;
  };

  const renderDefaultQuantityAndAddToBagButton = () => {
    const selectedVariant = isSendToMyself ? resolvedSelectedVariant : masterVariant;
    const addToBagProps = {
      product,
      quantity,
      variant: selectedVariant,
      btnParentClass: classNames(styles.bagBtnBlock, styles.bagBtnBlockLeft),
      showAddToBox,
      onSuccessCallback,
      handleQuantity,
      isDisabled: !product.purchasable || isVariantOutOfStock(),
      isColorAndSizeSelectionAvailable: isSendToMyself,
    };
    return (
      <>
        {renderQuantitySelector()}
        {renderAddToBagBtn(addToBagProps)}
        {renderRestrictionsModal({
          product,
          quantity,
          variant: resolvedSelectedVariant,
        })}
      </>
    );
  };

  const renderDefaultSizeAndQtyOptions = () => (
    <>
      <div className={styles.sizeOptionWrap}>
        {renderSizeVariants()}
        {renderSizeChartCTA()}
      </div>
      <ProductNotAvailableMessage canBuyProduct={canBuyProduct} name={name} />
      {showBulkOptions && renderBulkSizeAndQtyOptions({ showSizeGuide: false })}
      {!showBulkOptions && !isViewOnlyMode && (
        <div className={`${styles.qtyWithBagBtnRow} stickyDemoMode`}>
          {renderDefaultQuantityAndAddToBagButton()}
        </div>
      )}
    </>
  );

  const getTooltipMessage = (outOfStock) => {
    if (isColorPresent && isEmpty(optionTypeVariantMapping)) return "Please select color first";
    else if (outOfStock) return "Size not available";
  };

  const renderSizeBox = ({ key, presentation, onChange, disabled, id, onBlur, onFocus }) => (
    <TextField
      inputMode="numeric"
      id={key}
      prefix={
        <label htmlFor={key} className={styles.formLabel}>
          {presentation}
        </label>
      }
      value={sizeQtyMapping[id]?.qty || ""}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      classes={{
        wrapper: "inputWrapper",
        input: "inputField",
      }}
      placeholder="0"
      validator={normalizeCount}
      disabled={disabled}
    />
  );

  const minQtyForLockerAndBulk = useMemo(
    () => getMinOrderableQuantity(product?.variantsIncludingMaster),
    [product],
  );

  const renderSingleQtySelector = ({
    onChange = setProductQuantity,
    quantity: val = quantity,
    sizePresentation,
    minQty,
    showSizeGuide = false,
    outOfStock = false,
  }) => {
    const minQrderableQty = skipMinimumOrderableQuantity ? 1 : minQty;

    const isQtySelectorDisabled =
      (isSendToMyself && isColorPresent && isEmpty(optionTypeVariantMapping)) ||
      outOfStock ||
      !canBuyProduct;
    return (
      <>
        <div className={styles.renderSingleQtyRow}>
          <div className={styles.sizesHeading}>
            QTY{" "}
            <span className={styles.headingLabel}>
              {minQrderableQty > 1 ? `(MIN ${minQrderableQty})` : ""}
            </span>
          </div>
          {!!sizePresentation && (
            <div className={`${styles.sizesHeading} ${styles.qtySpacer} ${styles.singleSizeCont}`}>
              Size <span className={styles.headingLabel}>({sizePresentation})</span>
            </div>
          )}
          {showSizeGuide && !isViewOnlyMode && renderSizeChartCTA()}
        </div>
        {!isViewOnlyMode && (
          <div className={`${styles.qtyWithBagBtnRow} stickyDemoMode`}>
            <TippyTooltip
              content={getTooltipMessage(outOfStock)}
              isLightTheme={true}
              placement="top-end"
              arrow={true}
              disabled={!isQtySelectorDisabled}
              customClassName={styles.colorVariantTippy}
            >
              <span>
                <QuantitySelector
                  minOrderableQuantity={minQrderableQty}
                  quantity={val}
                  setQuantity={onChange}
                  restrictZeroInput={true}
                  className="defaultStyle"
                  disabled={isQtySelectorDisabled}
                  eventLocationAnalytics={{
                    track: true,
                    eventProperties: {
                      product_id: product.id,
                      product_type: getProductType(product),
                      category: getProductCategory(product),
                    },
                  }}
                />
              </span>
            </TippyTooltip>
            <div className={styles.singleQtyBtnCont}>{renderBulkAddToBagBtn()}</div>
          </div>
        )}
      </>
    );
  };

  const renderSizeLabelAndChart = (sizeLabel, minQty) => (
    <div className={styles.sizesGuideRow}>
      <div className={`${styles.sizesHeading} ${styles.textDefault}`}>
        {sizeProps.availData && (
          <>
            {sizeLabel}{" "}
            <span className={styles.headingLabel}>
              {!skipMinimumOrderableQuantity && minQty > 1 ? `(MIN QTY ${minQty})` : ""}
            </span>
          </>
        )}
      </div>
      {!isViewOnlyMode && renderSizeChartCTA()}
    </div>
  );

  const renderSizeOptions = (minQty) => {
    const onChangeVal = (id, val, priceIn) => {
      const newMapping = {
        ...sizeQtyMapping,
        [id]: { ...(sizeQtyMapping[id] ?? {}), qty: val, storePrice: priceIn, price: priceIn },
      };
      const newQty = getQuantityFromSizeMapping(newMapping);
      Object.keys(newMapping).forEach((idKey) => {
        newMapping[idKey].storePrice = getPriceFromBulkProduct(masterVariant, newQty);
      });
      setSizeQtyMapping(newMapping);
    };
    if (isSingleSize) {
      const { id, presentation, priceIn, outOfStock } = availableSizes[0];
      return renderSingleQtySelector({
        quantity: Number(sizeQtyMapping[id]?.qty),
        minQty,
        onChange: (val) => onChangeVal(id, val, priceIn),
        sizePresentation: presentation,
        outOfStock,
        showSizeGuide: true,
      });
    }
    return (
      <>
        {!showBulkOptions && renderSizeLabelAndChart("Sizes", minQty)}
        <div className={styles.formRowWrapper}>
          {(sizeProps.availData || []).map((variant) => {
            const { id, presentation, priceIn, outOfStock } = variant;
            const key = `size-option-${id}`;
            const onChange = (val) => onChangeVal(id, val, priceIn);

            if (isViewOnlyMode) {
              return (
                <div className={styles.inputWrapCont} key={id}>
                  <label className={styles.formLabel}>{presentation}</label>
                </div>
              );
            }

            if ((isColorPresent && isEmpty(optionTypeVariantMapping)) || outOfStock) {
              return (
                <div className={styles.inputWrapCont} key={id}>
                  <TippyTooltip
                    key={key}
                    content={getTooltipMessage(outOfStock)}
                    isLightTheme={true}
                    placement="top-start"
                    arrow={true}
                    customClassName={styles.colorVariantTippy}
                  >
                    <div>{renderSizeBox({ key, id, onChange, presentation, disabled: true })}</div>
                  </TippyTooltip>
                </div>
              );
            }
            return (
              <div className={styles.inputWrapCont} key={id}>
                {renderSizeBox({
                  key,
                  id,
                  onChange,
                  presentation,
                  onFocus: () => handleSizeQtyFocus(id),
                  onBlur: (ev) =>
                    handleSizeQtyBlur(ev, id, presentation, sizeProps.availData || []),
                  disabled: !canBuyProduct,
                })}
              </div>
            );
          })}
        </div>
        {!isViewOnlyMode && (
          <div className={styles.singleRowBtnCont}>{renderBulkAddToBagBtn()}</div>
        )}
      </>
    );
  };
  const renderMinQtyMessage = (minQty) => {
    if (!bulkAvailable) return null;
    return (
      <div className={styles.minQtyText}>
        Min order quantity is {minQty}, please add {minQty} items to add to bag.
      </div>
    );
  };

  const resolveBulkSelectedVariant = () => {
    if (!isSendToMyself) return masterVariant;
    if (isVariantLevelMoqFlow) return resolvedSelectedVariant;
    return selectedSizeVariant;
  };

  const isBulkAddDisabled = (qty) =>
    !product.purchasable ||
    (!skipMinimumOrderableQuantity && qty < minQty) ||
    isVariantOutOfStock() ||
    isMoqAddToBagBtnDisabled;

  const buildBulkAddToBagProps = (qty, isAddItemDisabled) => {
    const quantityLabel = qty > 1 ? ` (${qty}) ` : "";
    return {
      product,
      btnParentClass: styles.bagBtnBlock,
      btnLabel: !isEmpty(editProductIds) ? "Update bag" : `Add ${quantityLabel}to bag`,
      isDisabled: isAddItemDisabled || !canBuyProduct,
      totalQty: qty,
      totalPrice,
      price: value,
      hasSizeOptions: isVariantLevelMoqFlow ? false : hasSizeOptions,
      isColorPresent,
      sizeQtyMapping,
      variantsData,
      variantSelected: resolveBulkSelectedVariant(),
      variant: isVariantLevelMoqFlow ? resolvedSelectedVariant : optionTypeVariantMapping,
      onSuccessCallback: () => isEmpty(editProductIds) && resetSizeQtyMapping(),
      isColorAndSizeSelectionAvailable: isSendToMyself,
      isCustomizable,
    };
  };

  const renderBulkAddToBagBtn = (qty = quantity) => {
    const isAddItemDisabled = isBulkAddDisabled(qty);
    const addToBagProps = buildBulkAddToBagProps(qty, isAddItemDisabled);

    return (
      <>
        {renderAddToBagBtn(addToBagProps)}
        {!skipMinimumOrderableQuantity && qty < minQty && qty > 1 && renderMinQtyMessage(minQty)}
        {renderRestrictionsModal({
          renderAddToBagCta: (preferences) => (
            <AddToBagLockerBtn
              {...addToBagProps}
              preferences={preferences}
              onSuccessCallback={() => {
                addToBagProps.onSuccessCallback();
                toggleRestrictionsModal();
              }}
            />
          ),
        })}
      </>
    );
  };

  const renderBulkSizeAndQtyOptions = ({ showSizeGuide = true }) => {
    return (
      <div className={styles.sizesWrapper}>
        <ProductNotAvailableMessage canBuyProduct={canBuyProduct} name={name} />
        {hasSizeOptions
          ? renderSizeOptions(minQty)
          : renderSingleQtySelector({ minQty, showSizeGuide })}
        {isLockerShop && isCustomizable && (
          <div className={styles.mockupRequest}>Mockups can be requested from your bag.</div>
        )}
      </div>
    );
  };

  const renderVariantLevelMoqOptions = () => (
    <>
      <div className={styles.sizeOptionWrap}>
        {renderSizeVariants()}
        {renderSizeChartCTA()}
      </div>
      <ProductNotAvailableMessage canBuyProduct={canBuyProduct} name={name} />
      {renderSingleQtySelector({
        minQty,
        sizePresentation: resolvedSelectedVariant?.presentation,
      })}
    </>
  );

  const renderSizeAndQuantityOptions = () => {
    if (isVariantLevelMoqFlow) return renderVariantLevelMoqOptions();
    if (isLockerShop || bulkAvailable) return renderBulkSizeAndQtyOptions({ showSizeGuide: true });
    return renderDefaultSizeAndQtyOptions();
  };

  const displayTotalPrice =
    isPriceFormatInStoreCurrencyRequried &&
    formatPriceInDisplayStoreCurrency(totalPrice, storeCurrencyCode);

  const displayPrice = (() => {
    if (isPriceFormatInStoreCurrencyRequried) {
      const withPrefix = (amount) => `${amount} per unit`;
      if (bulkAvailable || hasTierPricing) {
        return withPrefix(display);
      }
      return withPrefix(
        formatPriceInDisplayStoreCurrency(
          getMinPriceForProduct(prices, totalPrice, quantity),
          storeCurrencyCode,
        ),
      );
    }
    return display;
  })();

  const renderColorAndSizeVariantSelector = () => {
    if (!isSendToMyself) {
      const variantValues = Object.values(variantsData);
      const avaialbleSizeData = isColorPresent ? allSizesVariants : variantValues;
      const colorVariantDataToShow =
        !isColorPresent || colorVariantData.length < 2 ? [] : colorVariantData;

      return (
        <>
          <SwagViewOnlyVariantDetails
            colorVariantData={colorVariantDataToShow}
            allSizesVariants={avaialbleSizeData}
            variantSelected={selectedSizeVariant}
            handleChange={handleSwagOptionSelectorChange}
            isPurchasable={canBuyProduct}
          />
          {hasBulkAvailable ? (
            <div className={styles.sizesWrapper}>
              {renderSingleQtySelector({ minQty: minQtyForLockerAndBulk })}
            </div>
          ) : (
            !isViewOnlyMode && (
              <div className={`${styles.qtyWithBagBtnRow} stickyDemoMode`}>
                {renderDefaultQuantityAndAddToBagButton()}
              </div>
            )
          )}
        </>
      );
    }
    return (
      <>
        {renderColorVariants()}
        {renderSizeAndQuantityOptions()}
      </>
    );
  };

  const renderProductDetailHeader = () => (
    <ProductDetailHeader
      displayPrice={!hidePriceDetail && displayPrice}
      displayTotalPrice={displayTotalPrice}
      showProductName={showProductName}
      product={product}
    />
  );

  const renderProductDetails = () => {
    return (
      <div className={`${styles.boxContainerFluid} boxContainerFluid`}>
        <div className={styles.productDescriptionWrapper}>
          {!isMobileDevice && renderProductDetailHeader()}
        </div>
        <VendorHolidayMessage vendor={vendorByCountry} />

        {!isSwagCreationShopFlow && (
          <div className={styles.colorSizeWrapper}>
            {!hideVariantSelectionSection
              ? renderColorAndSizeVariantSelector()
              : renderCustomVariantSelectionSection()}
          </div>
        )}
        <ProductDescriptionAccordion product={product} />
        {isSwagCreationShopFlow && renderPreferredColorAndSize()}
        <PropertiesAccordionList
          instructions={product?.productDetail?.orderInstructions}
          productId={product.id}
        />
        {!isSwagCreationShopFlow && <ProductInfoSection product={product} />}
        {!hideVariantSelectionSection &&
          sizeChartEnabled &&
          renderSizeChartModal(
            {
              data: { productName: name, sizeChart, sizeChartLink },
            },
            false,
          )}

        {(bulkAvailable || hasTierPricing) &&
          !hidePriceDetail &&
          discountTableData?.length > 1 &&
          !isSwagCreationShopFlow && <ProductDiscountTable tableData={discountTableData} />}

        {!isSwagCreationShopFlow && renderCatalogModeFooterLinks()}

        {isSwagCreationShopFlow && renderAddProducts()}
      </div>
    );
  };

  if (isMobileDevice) {
    return renderMobileView({
      renderHeader: renderProductDetailHeader,
      renderMobileProductDetails: renderProductDetails,
    });
  }

  return renderProductDetails();
}
